import { useState } from 'react';
import { DollarSign, X } from 'lucide-react';
import type { Project } from '@/types';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-hot-toast';

interface Props {
  project: Project;
  type: 'sales-rep' | 'partner';
  onClose: () => void;
  onSuccess: () => void;
}

export function MarkCommissionPaidModal({ project, type, onClose, onSuccess }: Props) {
  const expectedAmount = type === 'sales-rep'
    ? project.expectedCommission
    : project.partnerExpectedCommission;

  const [paidAmount, setPaidAmount] = useState(expectedAmount || 0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const functions = getFunctions();
      const functionName = type === 'sales-rep'
        ? 'crm_markSalesRepCommissionPaid'
        : 'crm_markPartnerCommissionPaid';
      const markPaid = httpsCallable(functions, functionName);

      await markPaid({
        projectId: project.id,
        paidAmount,
        notes,
      });

      toast.success(`${type === 'sales-rep' ? 'Sales rep' : 'Partner'} commission marked as paid`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to mark paid:', error);
      toast.error('Failed to mark commission as paid');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            Mark {type === 'sales-rep' ? 'Sales Rep' : 'Partner'} Commission Paid
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project
            </label>
            <p className="text-sm text-gray-900">{project.name}</p>
            <p className="text-xs text-gray-500">
              Expected: ${expectedAmount?.toLocaleString()}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Actual Amount Paid
            </label>
            <div className="relative">
              <DollarSign size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={paidAmount}
                onChange={(e) => setPaidAmount(Number(e.target.value))}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2"
                step="0.01"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={3}
              placeholder="Payment method, check number, etc."
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            disabled={submitting}
          >
            {submitting ? 'Marking Paid...' : 'Mark as Paid'}
          </button>
        </div>
      </div>
    </div>
  );
}
